/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from 'react';
import { useState } from 'react';
import { Button, Form, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faPlus, faCircleXmark, faPlay } from '@fortawesome/free-solid-svg-icons';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { createSegmentRequest, segmentDetailRequest, updateSegmentRequest } from '../../../redux/modules/contacts/contactSlice';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from '../../../component';
import XButton from '../../../component/button/XButton';
import { RootState } from '../../../redux/rootReducer';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useSidebar from '../../../hooks/useSidebar';
import { toaster } from '../../../utils/toast';
import { getPredefinedFieldRequest } from '../../../redux/modules/settings/settingsSlice';

interface Filter {
  id: number;
  field: string;
  operator: string;
  value: string;
  logical_operator: 'AND' | 'OR' | null;
}

interface SegmentFormValues {
  segmentName: string;
  filters: Filter[];
}

const LAST_ENGAGED_FIELD = 'LastEngagedAt';
const LAST_CONTACT_ADDED_FIELD = 'LastContactAdded';
const CUSTOM_FIELD_PREFIX = 'customField__';
const ROLLING_DATE_OPERATORS = ['withinLastDays', 'moreThanDaysAgo'];

const CreateSegment: React.FC = () => {
  const { segmentId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const draftValues = location.state?.draftValues as SegmentFormValues | undefined;
  const submittedDraftValues = useRef<SegmentFormValues | null>(null);
  const { createSegment,segmentdetails,editedSegment, error, loading } = useSelector((state: RootState) => state.contactlist);
  const { predefinedFieldList } = useSelector((state: RootState) => state.settings);
  const customFields: any[] = (predefinedFieldList as any)?.customFields || [];
  const [filtersExist, setFiltersExist] = useState(
    () => (draftValues?.filters.length ?? 0) > 0,
  );
  const { onShowSidebar } = useSidebar();
  const [reviewProgress, setReviewProgress] = useState(0);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (loading) {
      setReviewProgress(10);
      interval = setInterval(() => {
        setReviewProgress((prev) => (prev >= 95 ? 95 : prev + Math.max(1, Math.floor((96 - prev) * 0.15))));
      }, 200);
    } else {
      setReviewProgress(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading]);

  const validationSchema = Yup.object().shape({
    segmentName: Yup.string().trim().required('Segment Name is required').test(
      (value) => value.trim() !== ''
    ),
    filters: Yup.array().of(
      Yup.object().shape({
        field: Yup.string().notOneOf(['Select'], 'Field is required').required('Field is required'),
        operator: Yup.string().required('Operator is required'),
        value: Yup.string().when('operator', {
            is: 'hasNeverEngaged',
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.trim().required('Value is required'),
          }).test('rolling-date-days', 'Enter a positive whole number of days', function (value) {
              const { operator } = this.parent;
              if (!ROLLING_DATE_OPERATORS.includes(operator)) return true;
              return /^\d+$/.test(value || '') && Number(value) > 0;
            }),
        logical_operator: Yup.string().nullable(),
      })
    ),
  });

  const formik = useFormik<SegmentFormValues>({
    initialValues: {
      segmentName: draftValues?.segmentName || '',
      filters: draftValues?.filters || [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      submittedDraftValues.current = values;
      const locationId = localStorage.getItem('selectedLocation');
      const payload = {
        name: values.segmentName,
        filters: values.filters,
        location_id: locationId !== null ? locationId : ''
      }
      if (segmentId) {
        dispatch(updateSegmentRequest({segmentId:segmentId, updatedData:payload}));
      } else {
        // The API payload uses snake_case fields while CreateSegmentPayload is
        // currently typed with a different shape. Preserve the existing wire payload.
        dispatch(createSegmentRequest(payload as any));
      }
    },
  });

  useEffect(() => {
    if (formik.values.filters.length === 0) {
      formik.setFieldValue('filters', [
        {
          id: 1,
          field: 'Select',
          operator: 'Select',
          value: '',
          logical_operator: null,
        },
      ]);
      setFiltersExist(true);
    }
  }, [formik.values.filters]);

  useEffect(() => {
    if (createSegment && createSegment?.success === true) {
      toast(createSegment?.message);
      navigate('/org/contacts/reviewSegment', {
        state: { draftValues: submittedDraftValues.current },
      });
    }
    else if (!segmentId && error && error?.success === false) {
      toaster({success: false, message: error.message});
    }
    if(segmentId){
      if (editedSegment && editedSegment?.success === true) {
        toast(editedSegment?.message);
        navigate(`/org/contacts/reviewSegment/${segmentId}`);
      }
      else if (error && error?.success === false) {
        toast(error?.message)
      }
    }
  }, [error, createSegment,editedSegment])

  const handleAddFilter = (field: string) => {
    formik.setFieldValue('filters', [
      ...formik.values.filters,
      {
        id: formik.values.filters.length + 1,
        field,
        operator: 'Select',
        value: '',
        logical_operator: 'AND',
      },
    ]);
    setFiltersExist(true);

  };

  const handleRemoveFilter = (id: number) => {
    formik.setFieldValue(
      'filters',
      (formik.values.filters as Filter[]).filter((filter) => filter.id !== id)
    );
    setFiltersExist(!!formik.values.filters.length);
  };

  const handleFilterChange = (id: number, field: string, operator: string, value: string, logical_operator: string) => {
    formik.setFieldValue(
      'filters',
      formik?.values?.filters?.map((filter: Filter) =>
        filter.id === id
          ? { ...filter, field, operator, value, logical_operator: logical_operator as 'AND' | 'OR' }
          : filter
      )
    );
  };

  const isRollingDateFilter = (index: number) =>
    [LAST_ENGAGED_FIELD, LAST_CONTACT_ADDED_FIELD].includes(
      (formik.values.filters as Filter[])[index]?.field,
    );

  const isNeverEngagedFilter = (index: number) =>
    (formik.values.filters as Filter[])[index]?.operator === 'hasNeverEngaged';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };
  const isCustomFieldFilter = (index: number) =>
    (formik.values.filters as Filter[])[index]?.field?.startsWith(
      CUSTOM_FIELD_PREFIX,
    );

  // Returns the type of the selected custom field: 'text' | 'date' | 'boolean'
  const getCustomFieldType = (index: number): string => {
    if (!isCustomFieldFilter(index)) return 'text';
    const fieldName = (formik.values.filters as Filter[])[
      index
    ]?.field?.replace(CUSTOM_FIELD_PREFIX, '');
    const cf = customFields.find((c: any) => c.name === fieldName);
    return cf?.type || 'text';
  };

  useEffect(() => {
    dispatch(getPredefinedFieldRequest());
    if(segmentId){
      dispatch(segmentDetailRequest(segmentId))
    }
    return () => {
      formik.resetForm();
    };
  }, []);
  
  useEffect(() => {
    if (segmentdetails && segmentdetails.success) {
      const { segmentName, filters } = segmentdetails;
      const mappedFilters = filters.map((filter:any) => ({
        id: filter.id,
        field: filter.field,
        operator: filter.operator,
        value: filter.value,
        logical_operator: filter.logical_operator,
      }));
  
      formik.setValues({
        segmentName,
        filters: mappedFilters,
      });
    }
  }, [segmentdetails]);
  
  return (
    <div>
      <div className="main-title d-flex align-items-center justify-content-between ">
        <h2 className='back-text'>
          <span className="d-lg-none cursor-pointer" onClick={()=>onShowSidebar()}><FontAwesomeIcon icon={faBars} /></span>{segmentId? "Edit a Segment" :"Create a Segment"}</h2>

      </div>
      <div className="add-contact">
        <Form onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit(e);
        }}
        >   
          <Row>
            <Col xl={12} md={12} xs={12}>
              <Form.Group className="form-group" controlId="formBasicEmail">
                <Form.Label>Segment Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter segment name"
                  {...formik.getFieldProps('segmentName')}
                  onKeyDown={handleKeyDown}
                  isInvalid={formik.touched.segmentName && !!formik.errors.segmentName}
                />
                <Form.Control.Feedback type="invalid">{formik.errors.segmentName}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <div className="advance-search">
            {filtersExist && formik.values.filters?.map((filter: Filter, index: number) => (
              <div className="item" key={filter.id}>
                <div className="choose-option d-flex align-items-center justify-content-between">
                  {index > 0 && (
                    <>
                      <span className="text">
                        <span
                          className={filter.logical_operator === 'AND' ? 'active' : ''}
                          onClick={() => handleFilterChange(filter.id, filter.field, filter.operator, filter.value, 'AND')}
                        >
                          AND
                        </span>
                        <span 
                          className={filter.logical_operator === 'OR' ? 'active' : ''}
                          onClick={() => handleFilterChange(filter.id, filter.field, filter.operator, filter.value, 'OR')}
                        >
                          OR
                        </span>
                      </span>
                      <span onClick={() => handleRemoveFilter(filter.id)}>
                        <FontAwesomeIcon icon={faCircleXmark} />
                      </span>
                    </>
                  )}
                </div>
                <div className="inputs">
                  <Row>
                      <Col
                        xl={filter.field === "__customField__" || isCustomFieldFilter(index) ? 3 : 4}
                        lg={filter.field === "__customField__" || isCustomFieldFilter(index) ? 3 : 4}
                        md={filter.field === "__customField__" || isCustomFieldFilter(index) ? 6 : 4}
                        sm={filter.field === "__customField__" || isCustomFieldFilter(index) ? 6 : 4}
                        xs={12}
                      >
                      <Form.Group className="form-group" controlId={`field-${filter.id}`}>
                        <Form.Select
                          aria-label="Default select example"
                          {...formik.getFieldProps(`filters[${index}].field`)}
                          value={isCustomFieldFilter(index) ? "__customField__" : filter.field}
                          onChange={(event) => {
                            const nextField = event.target.value;
                            handleFilterChange(
                              filter.id,
                              nextField,
                              'Select',
                              '',
                              filter.logical_operator || 'AND'
                            );
                          }}
                          isInvalid={formik.touched.filters && formik.touched.filters[index] && !!(formik.errors.filters as any)?.[index]?.field}
                          >
                            <option value="">Select</option>
                            <option value="Tags">Tags</option>
                            <option value="Email">Email</option>
                            <option value="FirstName">First Name</option>
                            <option value="Address">Address</option>
                            <option value="Phone_Number">Phone Number</option>
                            <option value="DOB">DOB</option>
                            <option value={LAST_ENGAGED_FIELD}>Last Engaged</option>
                            <option value={LAST_CONTACT_ADDED_FIELD}>Last Contact Added</option>
                            {customFields.length > 0 && (
                              <option value="__customField__">
                                Custom Field
                              </option>
                            )}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {(formik.errors.filters as any)?.[index]?.field}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      {/* Custom Field sub-selector */}
                      {(filter.field === "__customField__" ||
                        isCustomFieldFilter(index)) && (
                        <Col xl={3} lg={3} md={6} sm={6} xs={12}>
                          <Form.Group
                            className="form-group"
                            controlId={`customField-${filter.id}`}
                          >
                            <Form.Select
                              aria-label="Select custom field"
                              value={
                                isCustomFieldFilter(index)
                                  ? filter.field.replace(
                                      CUSTOM_FIELD_PREFIX,
                                      "",
                                    )
                                  : ""
                              }
                              onChange={(event) => {
                                const selectedFieldName = event.target.value;
                                handleFilterChange(
                                  filter.id,
                                  selectedFieldName
                                    ? `${CUSTOM_FIELD_PREFIX}${selectedFieldName}`
                                    : "__customField__",
                                  "Select",
                                  "",
                                  filter.logical_operator || "AND",
                                );
                              }}
                            >
                              <option value="">Select custom field</option>
                              {customFields.map((cf: any) => (
                                <option key={cf.id} value={cf.name}>
                                  {cf.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      )}
                      <Col
                        xl={filter.field === "__customField__" || isCustomFieldFilter(index) ? 3 : 4}
                        lg={filter.field === "__customField__" || isCustomFieldFilter(index) ? 3 : 4}
                        md={filter.field === "__customField__" || isCustomFieldFilter(index) ? 6 : 4}
                        sm={filter.field === "__customField__" || isCustomFieldFilter(index) ? 6 : 4}
                        xs={12}
                      >
                      <Form.Group className="form-group" controlId={`operator-${filter.id}`}>
                        <Form.Select
                          aria-label="Default select example"
                          {...formik.getFieldProps(`filters[${index}].operator`)}
                          disabled={filter.field === "__customField__"}
                          onChange={(event) => {
                            const nextOperator = event.target.value;
                            handleFilterChange(
                              filter.id,
                              filter.field,
                              nextOperator,
                              nextOperator === 'hasNeverEngaged' ? '' : filter.value,
                              filter.logical_operator || 'AND'
                            );
                          }}
                          isInvalid={formik.touched.filters && formik.touched.filters[index] &&!!(formik.errors.filters as any)?.[index]?.operator}
                        >
                          <option value="Select">Select</option>
                          {isRollingDateFilter(index) && (
                            <>
                              <option value="withinLastDays">Within last days</option>
                              <option value="moreThanDaysAgo">More than days ago</option>
                              {filter.field === LAST_ENGAGED_FIELD && (
                                <option value="hasNeverEngaged">Has never engaged</option>
                              )}
                            </>
                          )}
                          {/* Custom field type-aware operators */}
                          {isCustomFieldFilter(index) &&
                              getCustomFieldType(index) === "date" && (
                            <>
                              <option value="Contains">Contains</option>
                              <option value="DoesNotContain">Does not contain</option>
                              <option value="isExactly">Is Exactly</option>
                              <option value="notExactly">Not Exactly</option>
                              <option value="isBefore">Is Before</option>
                              <option value="isAfter">Is After</option>
                            </>
                            )}
                            {isCustomFieldFilter(index) &&
                              getCustomFieldType(index) === "boolean" && (
                                <>
                                  <option value="Contains">Contains</option>
                                  <option value="DoesNotContain">
                                    Does not contain
                                  </option>
                                  <option value="isExactly">Is Exactly</option>
                                  <option value="notExactly">
                                    Not Exactly
                                  </option>
                                </>
                              )}
                            {isCustomFieldFilter(index) &&
                              getCustomFieldType(index) === "text" && (
                                <>
                                  <option value="Contains">Contains</option>
                                  <option value="DoesNotContain">
                                    Does not contain
                                  </option>
                                  <option value="isExactly">Is Exactly</option>
                                  <option value="notExactly">
                                    Not Exactly
                                  </option>
                                </>
                              )}
                            {!isRollingDateFilter(index) &&
                              !isCustomFieldFilter(index) && (
                                <>
                                  <option value="Contains">Contains</option>
                                  <option value="DoesNotContain">
                                    Does not contain
                                  </option>
                                  <option value="isExactly">Is Exactly</option>
                                  <option value="notExactly">
                                    Not Exactly
                                  </option>
                              {(formik.values.filters as any[])[index]?.field === 'DOB' && (
                                <>
                                  <option value="isBefore">Is Before</option>
                                  <option value="isAfter">Is After</option>
                                </>
                              )}
                            </>
                          )}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{(formik.errors.filters as any)?.[index]?.operator}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                      <Col
                        xl={filter.field === "__customField__" || isCustomFieldFilter(index) ? 3 : 4}
                        lg={filter.field === "__customField__" || isCustomFieldFilter(index) ? 3 : 4}
                        md={filter.field === "__customField__" || isCustomFieldFilter(index) ? 6 : 4}
                        sm={filter.field === "__customField__" || isCustomFieldFilter(index) ? 6 : 4}
                        xs={12}
                      >
                      <Form.Group className="form-group" controlId={`value-${filter.id}`}>
                          {/* Boolean custom field: true/false select */}
                          {isCustomFieldFilter(index) &&
                          getCustomFieldType(index) === "boolean" ? (
                            <Form.Select
                              {...formik.getFieldProps(
                                `filters[${index}].value`,
                              )}
                              isInvalid={
                                formik.touched.filters &&
                                formik.touched.filters[index] &&
                                !!(formik.errors.filters as any)?.[index]?.value
                              }
                            >
                              <option value="">Select value</option>
                              <option value="true">True</option>
                              <option value="false">False</option>
                            </Form.Select>
                          ) : (
                            <Form.Control
                              type={
                                isCustomFieldFilter(index) &&
                                getCustomFieldType(index) === "date"
                                  ? "date"
                                  : (formik.values.filters as any[])?.[index]
                                        ?.field === "DOB"
                                    ? "date"
                                    : isRollingDateFilter(index)
                                      ? "number"
                                      : "text"
                              }
                              min={isRollingDateFilter(index) ? 1 : undefined}
                              step={isRollingDateFilter(index) ? 1 : undefined}
                              placeholder={isRollingDateFilter(index) ? 'Enter days (e.g. 90)' : 'Enter value'}
                              disabled={isNeverEngagedFilter(index) || filter.field === "__customField__"}
                              {...formik.getFieldProps(`filters[${index}].value`)}
                              isInvalid={formik.touched.filters && formik.touched.filters[index] && !!(formik.errors.filters as any)?.[index]?.value}
                        />
                      )}
                      <Form.Control.Feedback type="invalid">{(formik.errors.filters as any)?.[index]?.value}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </div>
            ))}
                <Button
                  variant="primary"
                  type="submit"
                  className="btn-theme mb-4"
                  onClick={() => handleAddFilter('')}
                ><FontAwesomeIcon icon={faPlus} className='ms-0' /> Add Filter
                </Button>
              </div>
              <div className="btns">
              <div className="d-flex justify-content-between align-items-center">
              <Button
                variant="primary"
                className="btn-theme btn-border"
                onClick={() => navigate("/org/contacts/segments")}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faPlay} />
                Cancel
              </Button>
              <XButton
                variant="primary"
                type="submit"
                buttonText={<>Review Segment <FontAwesomeIcon icon={faPlay} /></>}
                loading={loading}
                loadingText={reviewProgress > 0 ? `${reviewProgress}% Processing...` : "Loading..."}
                disabled={loading}
              />
                {/* <Button variant="primary" type="submit" className='btn-theme' >
                  {loading && <Loader />}
                  Submit<FontAwesomeIcon icon={faPlay} />
                </Button> */}
              </div>
          </div>
        </Form>
      </div>
    </div>
  )
}

export default CreateSegment
